import type { ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { COOKIE_FIREBASE_SESSION } from '@application/constants';
import { ENV, environmentVariable } from '@application/env';
import { useAuthState } from '@application/state';
import { cookies, useRouter, useShowToast } from '@application/utils';
import { useCallback, useMemo } from 'react';

export const useClearAccount = () => {
  const { showToast } = useShowToast();
  const { translate, navigateToRoute } = useRouter();

  const authState = useAuthState();

  const apolloClient = useApolloClient();

  const domain = useMemo(() => {
    const env = environmentVariable(ENV.STAGE);
    if (env === 'staging') {
      return '.tipaw.com';
    }
    if (['development', 'dev', 'localhost'].includes(env)) {
      return 'localhost';
    }
    return 'tipaw.com';
  }, []);

  const clearAccount = useCallback(() => {
    cookies.remove(COOKIE_FIREBASE_SESSION, {
      path: '/',
      domain,
    });
    authState.setState({ token: undefined });
    apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'accountMe' });
    apolloClient.cache.gc();

    if (typeof window !== 'undefined') {
      window.apolloState = {};
    }
  }, [apolloClient.cache, authState, domain]);

  const redirectOnTokenErrorHandler = useCallback(
    (err: ApolloError) => {
      const message =
        (err.networkError as any)?.result?.errors?.[0]?.message ??
        err.graphQLErrors?.[0]?.message ??
        err.message;
      if (
        message === 'auth/token-invalid' ||
        message === 'auth/no-token' ||
        message === 'auth/account-not-found' ||
        message === 'auth/facebook-token-invalid' ||
        message === 'auth/google-token-invalid' ||
        message === 'auth/firebase-token-invalid' ||
        message === 'Context creation failed: auth/token-invalid' ||
        message === 'Access denied! You need to be authorized to perform this action!'
      ) {
        clearAccount();
        showToast(translate('common.toasts.apolloError.auth/token-invalid'), {
          type: 'error',
          toastId: 'auth/token-invalid',
        });
        navigateToRoute('login');
      }
    },
    [clearAccount, navigateToRoute, showToast, translate],
  );

  return { redirectOnTokenErrorHandler, clearAccount };
};
